import Vue from "vue";
import {
  getDatabase,
  ref,
  query,
  get,
  update,
  push,
  set,
  remove,
  child,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  orderByKey,
  endBefore,
  limitToLast,
  startAfter,
  serverTimestamp,
} from "firebase/database";

import { replyToUser } from "@/common/functions";

import { ObjectToSortedArrayReverse } from "@/helpers/utils";

import { FETCH, CREATE, UPDATE, REMOVE, LISTEN, UNLISTEN, RESPOND } from "../../actions";
import { SET_ITEM, DEL_ITEM } from "../../mutations";

const LIMIT_PAGE_MESSAGES = 10;

const database = getDatabase();

const getInitState = () => ({
  items: {},
});

const getters = {
  itemsSorted: (state) => () => {
    return ObjectToSortedArrayReverse(state.items, "createdAt");
  },
  itemsSortedByTaskId: (_, getters) => (taskId) => {
    const itemsSorted = getters.itemsSorted();
    return itemsSorted.filter((c) => c.taskId === taskId);
  },
};

const mutations = {
  [SET_ITEM](state, { id, ...payload }) {
    Vue.set(state.items, id, payload);
  },
  [DEL_ITEM](state, { id }) {
    Vue.delete(state.items, id);
  },
};

const actions = {
  async [FETCH]({ commit }, payload) {
    console.log("FETCH");
    const { projectId, lastKey, taskId } = payload;
    console.log(`projects/${projectId}/tasksMessages/${taskId}`);
    const resultRef = lastKey
      ? query(
          ref(database, `projects/${projectId}/tasksMessages/${taskId}`),
          orderByKey(),
          endBefore(lastKey),
          limitToLast(LIMIT_PAGE_MESSAGES)
        )
      : query(ref(database, `projects/${projectId}/tasksMessages/${taskId}`), orderByKey(), limitToLast(LIMIT_PAGE_MESSAGES));

    const snapshot = await get(resultRef);

    const data = snapshot.val();

    // if (data) {
    //   Object.entries(data).forEach(([messageId, messageData]) => {
    //     commit(SET_ITEM, { id: messageId, taskId, ...messageData });
    //   });
    // }
    if (data) {
      Object.entries(data).forEach(([messageId, messageData]) => {
        const { chunks, isStream = false, ...others } = messageData;

        if (isStream) others.content = Object.values(chunks).join("");
        commit(SET_ITEM, { id: messageId, taskId, ...others });
      });
    }

    return { isSuccess: true, data };
  },
  async [CREATE]({ rootGetters }, payload) {
    const uid = rootGetters["auth/uid"];
    const createdAt = serverTimestamp();

    try {
      const { projectId, taskId } = payload;

      const id = push(child(ref(database), `projects/${projectId}/tasksMessages/${taskId}`)).key;
      await set(ref(database, `projects/${projectId}/tasksMessages/${taskId}/${id}`), { ...payload, createdAt, uid, id });

      return { isSuccess: true, id };
    } catch (error) {
      console.log("ERR", error);
      return { isSuccess: false, error };
    }
  },
  async [UPDATE]({ rootGetters }, payload) {
    const updatedBy = rootGetters["auth/uid"];

    const { id, type = null, content } = payload;
    const updatedAt = serverTimestamp();

    try {
      const { projectId, taskId } = payload;
      await update(ref(database, `projects/${projectId}/tasksMessages/${taskId}/${id}`), {
        type,
        content,
        updatedAt,
        updatedBy,
        id,
      });

      return { isSuccess: true, id };
    } catch (error) {
      return { isSuccess: false, error };
    }
  },
  async [REMOVE](_, payload) {
    const { projectId, id } = payload;

    try {
      const { taskId } = payload;
      await remove(ref(database, `projects/${projectId}/tasksMessages/${taskId}/${id}`));

      return { isSuccess: true, id };
    } catch (error) {
      return { isSuccess: false, error };
    }
  },

  // async [LISTEN]({ commit }, payload) {
  //   const { projectId, taskId } = payload;

  //   const resultRef = query(
  //     ref(database, `projects/${projectId}/tasksMessages/${taskId}`),
  //     orderByKey(),
  //     limitToLast(LIMIT_PAGE_MESSAGES)
  //   );

  //   onChildAdded(resultRef, (snapshot) => {
  //     const references = listReferences(snapshot.val());
  //     commit(SET_ITEM, { taskId, id: snapshot.key, ...snapshot.val(), references });
  //   });

  //   onChildChanged(resultRef, (snapshot) => {
  //     commit(SET_ITEM, { taskId, id: snapshot.key, ...snapshot.val() });
  //   });

  //   onChildRemoved(resultRef, (snapshot) => {
  //     commit(DEL_ITEM, { taskId, id: snapshot.key });
  //   });
  // },
  async [LISTEN]({ commit }, payload) {
    const { projectId, taskId, lastKey } = payload;

    const resultRef = lastKey
      ? query(ref(database, `projects/${projectId}/tasksMessages/${taskId}`), orderByKey(), startAfter(lastKey))
      : query(ref(database, `projects/${projectId}/tasksMessages/${taskId}`), orderByKey(), limitToLast(LIMIT_PAGE_MESSAGES));

    onChildAdded(resultRef, (snapshot) => {
      commit(SET_ITEM, { taskId, id: snapshot.key, ...snapshot.val() });
    });

    onChildChanged(resultRef, (snapshot) => {
      const { chunks, isStream = false, ...others } = snapshot.val();

      if (!isStream) {
        commit(SET_ITEM, { taskId, id: snapshot.key, ...snapshot.val() });
      } else {
        const content = Object.values(chunks).join("");
        commit(SET_ITEM, { taskId, id: snapshot.key, ...others, content });
      }
    });

    onChildRemoved(resultRef, (snapshot) => {
      commit(DEL_ITEM, { taskId, id: snapshot.key });
    });
  },
  async [UNLISTEN]() {},

  async [RESPOND]({ rootGetters }, payload) {
    const languageFamily = rootGetters["auth/languageFamily"];
    const { messages, ...others } = payload;

    // TODO: Sanitized messages should only keep the keys { role, content } for each item of the array messages
    const messagesSanitized = messages.map((message) => ({
      role: message.role,
      content: message.content,
    }));

    try {
      const { data } = await replyToUser({ ...others, messages: messagesSanitized, languageFamily });

      return { isSuccess: true, ...data };
    } catch (error) {
      const { details: errorCode } = JSON.parse(JSON.stringify(error));
      return { isSuccess: false, errorCode };
    }
  },
};

export default {
  namespaced: true,
  state: getInitState(),
  getters,
  mutations,
  actions,
};
